import { Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AddressBook, AddressBookQuery, AddressBookService } from '@fgb/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { iif } from 'rxjs';
import { toContact, Contact } from '../../contact-management.helper';
import { TranslateService } from '@ngx-translate/core';
import { FGBValidators } from '@fgb/portal-component-library/src/lib/shared/validators';


/**
 * CRUD modal for contact management
 */
@Component({
  selector: 'fgbcl-contact-crud-modal',
  templateUrl: './contact-crud-modal.component.html',
  styleUrl: './contact-crud-modal.component.scss'
})
export class ContactCrudModalComponent implements OnInit{
  // Transform data to Contact, but doesnt transforming if used in modal or not using input ex. [contact]="contact"
  @Input({ transform: toContact }) contact: AddressBook;
  @Input() deleteContact: boolean = false;
  loading: boolean = false;
  isNew: boolean = false;
  errorMessage: string = '';
  contactForm: FormGroup;
  contactList: AddressBook[] = [];
  private fb: FormBuilder = inject(FormBuilder);
  private modalRef: NgbActiveModal = inject(NgbActiveModal);
  private translateService: TranslateService = inject(TranslateService);
  private addressService: AddressBookService = inject(AddressBookService);
  private addressBookQuery: AddressBookQuery = inject(AddressBookQuery);


  ngOnInit(): void {
    // Transform manually since it's not transforming Input using componentInstance when used in modal
    this.contact = toContact(this.contact);
    // If contact Id is null, then this user is new
    this.isNew = this.contact.Id === null;
    this.addressBookQuery.selectAllContacts().subscribe(list => {
      this.contactList = list;
      // Initialize form with contact data
      this._initializeForm(this.contact);
    });
  }

  /**
   * Initialize form and populate with contact data
   */
  private _initializeForm(o?: Contact): void {
    this.contactForm = this.fb.group({  
      Id: [o?.Id],
      FullName: [o?.FullName, [Validators.required, FGBValidators.Empty, Validators.pattern("^[a-zA-Z ,.'-]+$")]],
      EmailAddress: [o?.EmailAddress, [Validators.required, Validators.pattern(/^[a-zA-Z0-9._]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/), contactExistValidator(this.contactList)]],
      PortalId: [o?.PortalId],
      RelationTypeId: [o?.RelationTypeId]
    });
  }

  /**
   * Either add new contact or update existing contact
   */
  submit(): void{
    this.errorMessage = '';
    this.contactForm.markAllAsTouched();

    if( this.contactForm.valid ){
      const { Id, FullName, EmailAddress } = this.contactForm.value;
      // If isNew is true, add new contact, else update existing
      const request$ = iif(() => this.isNew, this.addressService.add(FullName, EmailAddress), this.addressService.update(Id, FullName, EmailAddress));
  
      this.loading = true;
      // Subscribe to request, no need to unsubscribe since it will be completed whenever we are calling any api calls like POST, PUT, DELETE.etc... 
      // Unless its from a service that returns an observable like from akita store which doesn't complete
      request$.subscribe({
        next: () => {
          this.closeModal();
        },
        error: (error) => {
          // Add generic error message, user doesn't care about specific error or what type of error it is. It is only for debugging purposes
          this.errorMessage = this.translateService.instant('contact.crud.modal.error.message');
        },
        complete: () => {
          this.loading = false;
        }
      });
    }

  }

  /**
   * Delete the contact
   */
  delete(): void{
    this.loading = true;
    this.addressService.remove(this.contact.Id).subscribe({
      complete: () => {
        this.loading = false;
        this.closeModal();
      }
    });
  }

  /**
   * Close the modal
   */
  closeModal(): void{
    this.modalRef.close();
  }
  
}

export function contactExistValidator(contacts: AddressBook[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const id = control.parent?.get('Id')?.value;
    if( !id && contacts?.length > 0 && contacts.find(contact => contact.EmailAddress === control.value) ){
      return { contactExist: true };
    }
    return null;
  };
}
